import React from 'react';
import Tool from './Tool';
import ToolButton from '../toolbars/ToolButton';
import styles from '../styles.module.css';
import { humanize, underscore } from 'inflected';
import ColorSwatch from './ColorSwatch';
import classnames from 'classnames';
import { fabric } from 'fabric';
import { isShape, isWireframe } from '../objects/guards';

const nameForType = type => {
  switch (type) {
    case 'note':
      return 'Sticky note';
    default:
      return humanize(underscore(type));
  }
};

const typeForObject = (obj: fabric.Object) => {
  if (isShape(obj)) {
    return obj.shapeName;
  } else if (isWireframe(obj)) {
    return obj.wireframe.wireframeConstructor.externalName();
  } else {
    return obj.type;
  }
};

const countObjects = selection => {
  // Count of objects indexed by object name.
  const counts = { shapes: {}, colors: {} };

  selection.forEach(object => {
    const type = typeForObject(object);

    counts.shapes[type] ||= 0;
    counts.shapes[type]++;

    if (object.fill && object.fill !== 'transparent') {
      counts.colors[object.fill] ||= 0;
      counts.colors[object.fill]++;
    }
  });

  return counts;
};

// No ApplicationView here because we don't want to memoize so that we rerender
// when the selection changes internally.
const FilterMenuButton = ({ controller, selection }) => {
  const counts = countObjects(selection);

  // Don't show the menu if only one type of object is selected.
  if (
    Object.keys(counts.shapes).length <= 1 &&
    Object.keys(counts.colors).length <= 1
  )
    return <></>;

  return (
    <aha-menu style={{ minWidth: 40, minHeight: 40 }}>
      <ToolButton slot='control' className={styles.toolButtonWide}>
        <aha-icon icon='fa-regular fa-filter' />{' '}
        <aha-badge flavor='gray' style={{ alignSelf: 'center', marginLeft: 5 }}>
          {selection.getObjects().length}
        </aha-badge>
      </ToolButton>
      <aha-menu-content>
        <aha-menu-group label='Filter selection'>
          {Object.keys(counts.shapes)
            .sort((a, b) => counts.shapes[b] - counts.shapes[a])
            .map(objectType => (
              <aha-menu-item
                key={objectType}
                onClick={() =>
                  controller.actionApplyTool(ToolFilter, { objectType })
                }
              >
                <a className={styles.menuItemWithShortcut}>
                  <span className={styles.menuItemName}>
                    <aha-icon icon='fa-regular fa-shapes' />{' '}
                    {nameForType(objectType)}{' '}
                    <aha-badge>{counts.shapes[objectType]}</aha-badge>
                  </span>
                </a>
              </aha-menu-item>
            ))}

          {counts.colors && (
            <aha-menu-item>
              <aha-menu>
                <aha-button kind='link' slot='control'>
                  <span
                    style={{
                      display: 'inline-block',
                      background:
                        'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)',
                      width: 15,
                      height: 15,
                      borderRadius: 10,
                      marginRight: 5,
                      alignSelf: 'center',
                    }}
                  />
                  Color
                </aha-button>
                <aha-menu-content>
                  {Object.keys(counts.colors)
                    .sort((a, b) => counts.colors[b] - counts.colors[a])
                    .map(color => (
                      <aha-menu-item
                        key={color}
                        onClick={() =>
                          controller.actionApplyTool(ToolFilter, { color })
                        }
                      >
                        <a className={styles.menuItemWithShortcut}>
                          <span
                            className={classnames(
                              styles.menuItemName,
                              styles.menuItemCenterBadge
                            )}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <ColorSwatch color={color} swatchIsInToolbar />

                            <aha-badge>{counts.colors[color]}</aha-badge>
                          </span>
                        </a>
                      </aha-menu-item>
                    ))}
                </aha-menu-content>
              </aha-menu>
            </aha-menu-item>
          )}
        </aha-menu-group>
      </aha-menu-content>
    </aha-menu>
  );
};

class ToolFilter extends Tool {
  static toolName = 'Filter';

  static buttonComponent(): ((args: any) => JSX.Element) | null {
    return FilterMenuButton;
  }

  static action({ controller, selection, objectType, color }) {
    const canvas = controller.canvas;
    const newObjects = selection
      .getObjects()
      .filter(
        obj =>
          (objectType && typeForObject(obj) === objectType) ||
          (color && obj.fill === color)
      );

    canvas.discardActiveObject();
    if (newObjects.length === 1) {
      canvas.setActiveObject(newObjects[0]);
    } else if (canvas.size() > 1) {
      const sel = new fabric.ActiveSelection(newObjects, {
        canvas,
      });
      canvas.setActiveObject(sel);
    }
    canvas.requestRenderAll();
  }
}

export default ToolFilter;
